import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { getFlightAddOnFees } from 'agentHub/utils/flightCommission'
import config from 'constants/config'
import { sum } from 'lib/array/arrayUtils'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { AGENT_HUB_PAYMENT_MODE, PAYMENT_OPTIONS } from 'constants/payment'
import { floatify } from 'lib/maths/mathUtils'
import { createSelector } from 'reselect'
import { checkoutAccommodationOfferView } from '../view/accommodation'
import { getCarHireItems } from '../view/carHire'
import getPartnershipModifierAmount from './getPartnershipModifierAmount'
import getCheckoutTotal from './getCheckoutTotal'
import getCheckoutTotals from './getCheckoutTotals'
import getCreditPayableAmount from './getCreditPayableAmount'
import getCreditPayAmount from './getCreditPayAmount'
import getCommissionPaymentAmount from './getCommissionPaymentAmount'
import getDiscountTotal, { getVoucherDiscountTotal } from './getDiscountTotal'
import getMerchantFeeAmount from './getMerchantFeeAmount'
import getPayableTotal from './getPayableTotal'
import getServiceFeeAmount from './getServiceFeeAmount'
import { getCruiseItems } from '../cruiseSelectors'
import getMobileAppDiscountTotalsView from './getMobileAppDiscountTotalsView'
import { getCheckoutLoyaltyBurnTotalInCurrency, getCheckoutLoyaltyBurnTotalInPoints } from '../loyalty/getCheckoutLoyaltyBurnTotals'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import getGiftCardPayAmount from './getGiftCardPayAmount'
import { getPayAtPropertyAmount } from './propertyCollectSelectors'
import { getIncentiveCommissionBreakdown } from 'lib/agentHub/agentHubUtils'

const getCheckoutCruiseBrochure = createSelector(
  (state: App.State) => getCruiseItems(state),
  (cruiseItems) => {
    if (cruiseItems.length) {
      return cruiseItems.map((cruiseItem) => {
        const totalPassengers = Object.values(cruiseItem.occupancy).reduce((a, b) => a + b, 0)
        const brochurePrice = cruiseItem.brochureInfo?.price || 0
        return floatify(brochurePrice * totalPassengers)
      }).reduce((total, currentPrice) => floatify(total + currentPrice), 0)
    }
    return 0
  },
)

const getCheckoutTotalsView = createSelector(
  (state: App.State) => getCheckoutTotals(state),
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => getDiscountTotal(state),
  (state: App.State) => getVoucherDiscountTotal(state),
  (state: App.State) => getCreditPayableAmount(state),
  (state: App.State) => getCreditPayAmount(state),
  (state: App.State) => getGiftCardPayAmount(state),
  (state: App.State) => state.checkout.payment.partnerships.velocity,
  (state: App.State) => getPartnershipModifierAmount(state),
  (state: App.State, merchantFeePaymentType: App.MerchantFeePaymentType | null = null) => getMerchantFeeAmount(state, state.checkout.payment.paymentSelected, merchantFeePaymentType),
  (state: App.State) => state.checkout.payment.paymentSelected,
  (state: App.State) => getServiceFeeAmount(state),
  (state: App.State) => getCheckoutCruiseBrochure(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getCommissionPaymentAmount(state),
  (state: App.State) => getCarHireItems(state),
  (state: App.State) => getMobileAppDiscountTotalsView(state),
  (state: App.State) => getCheckoutLoyaltyBurnTotalInCurrency(state),
  (state: App.State) => getCheckoutLoyaltyBurnTotalInPoints(state),
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (state: App.State) => getPayAtPropertyAmount(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.commissionData,
  (
    checkoutTotals,
    total,
    payableTotal,
    discount,
    voucherDiscountTotal,
    amountPayableByCredit,
    creditPaymentAmount,
    giftCardPaymentAmount,
    velocityState,
    partnershipModifierAmount,
    merchantFee,
    paymentSelected,
    serviceFee,
    cruiseBrochurePrice,
    accommodationViews,
    commissionTotal,
    carHireItems,
    mobileAppDiscountTotals,
    loyaltyBurnTotalInCurrency,
    loyaltyBurnTotalInPoints,
    agentHubPaymentMode,
    payAtPropertyAmount,
    checkoutItems,
    commissionData,
  ): App.WithDataStatus<App.Checkout.TotalsView> => {
    const {
      price,
      value,
      surcharge,
      taxesAndFees,
      otherFees,
      propertiesFees,
      carHirePaidOnPickUpFees,
      carHirePayOnArrival,
      carHireAddonFees,
      paidPrice,
      newPrice,
      memberPrice,
      paymentScheduleAdjustment,
    } = checkoutTotals.data

    // need to adjust values to account for value possibly being undefined
    const adjPrice = price ?? 0
    const adjSurcharge = surcharge ?? 0
    const adjValue = value ?? 0
    const adjCarHirePaidOnPickUpFees = carHirePaidOnPickUpFees ?? 0
    const adjCarHireAddonsFees = carHireAddonFees ?? 0
    const adjCarHirePayOnArrival = carHirePayOnArrival ?? 0

    const flightAddOns = isAgentHubEnabled
      ? floatify(sum(checkoutItems.filter(isFlightItem), item => getFlightAddOnFees(item.otherFees)))
      : 0
    let subTotal = adjPrice + adjSurcharge - taxesAndFees + adjCarHireAddonsFees + adjCarHirePayOnArrival + flightAddOns
    // Need to round to avoid long decimals
    subTotal = floatify(subTotal)
    const totalValue = adjValue + adjSurcharge + cruiseBrochurePrice

    const hideDiscountPercentage = accommodationViews.data.some(view => 'offer' in view && view.offer &&
      'isDiscountPillHidden' in view.offer && view.offer.isDiscountPillHidden,
    )

    // Value includes `taxesAndFees` and (fee-inclusive) property fees, so the base
    // price the discount % compares against must include both — otherwise the saved %
    // is computed from a fee-inclusive value over a fee-exclusive base and overstated.
    const propertyFees = otherFees?.propertyFees ?? 0
    const basePrice = subTotal + taxesAndFees + adjCarHirePaidOnPickUpFees + propertyFees
    const savedTotal = totalValue - basePrice
    const savedPercentage = hideDiscountPercentage ? 0 : getValueOffPercent(totalValue, basePrice)

    const payableServiceFee = [PAYMENT_OPTIONS.DEPOSIT, PAYMENT_OPTIONS.PAYMENT_SCHEDULE, PAYMENT_OPTIONS.DYNAMIC_PAYMENT_SCHEDULE, PAYMENT_OPTIONS.GROUP_PAYMENT].includes(paymentSelected as PAYMENT_OPTIONS) ? serviceFee : 0

    const totalDiscount = discount + voucherDiscountTotal

    const grandTotalBeforeDiscount = floatify(
      total -
      creditPaymentAmount -
      giftCardPaymentAmount -
      loyaltyBurnTotalInCurrency -
      partnershipModifierAmount +
      adjCarHirePaidOnPickUpFees +
      merchantFee +
      payableServiceFee +
      adjCarHirePayOnArrival,
    )

    let grandTotal = grandTotalBeforeDiscount

    if (totalDiscount > grandTotal) {
      grandTotal = 0
    } else {
      grandTotal -= totalDiscount
    }

    // Need to round to avoid long decimals
    grandTotal = floatify(grandTotal)

    // The gross total must be independent of any voucher applied
    // vouchers reduce the customer's net payable, not the gross amount.
    let grandTotalExcludingVoucher = grandTotalBeforeDiscount
    if (discount > grandTotalExcludingVoucher) {
      grandTotalExcludingVoucher = 0
    } else {
      grandTotalExcludingVoucher -= discount
    }
    grandTotalExcludingVoucher = floatify(grandTotalExcludingVoucher)

    let payableNow = payableTotal - creditPaymentAmount - giftCardPaymentAmount - partnershipModifierAmount + merchantFee + payableServiceFee - loyaltyBurnTotalInCurrency - payAtPropertyAmount

    if (config.agentHub.isEnabled) {
      if (totalDiscount > payableNow) {
        payableNow = 0
      } else if (agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_NET_AMOUNT) {
        payableNow -= totalDiscount
      }
    }

    if (!config.agentHub.isEnabled) {
      if (totalDiscount > payableNow) {
        payableNow = 0
      } else {
        payableNow -= totalDiscount
      }
    }

    payableNow = floatify(payableNow)

    const velocityPointsApplied = (velocityState?.burn?.isApplied) ? velocityState.burn.pointsBurned : null

    return {
      hasRequiredData: checkoutTotals.hasRequiredData,
      data: {
        savedPercentage: Number.isFinite(savedPercentage) ? savedPercentage : 0,
        savedTotal,
        amountPayableByCredit,
        creditPaymentAmount,
        giftCardPaymentAmount,
        otherFees,
        propertiesFees,
        taxesAndFees,
        subTotal,
        commissionTotal,
        commissionGrossTotal: grandTotalExcludingVoucher + commissionTotal + (creditPaymentAmount),
        incentiveCommissionBreakdown: getIncentiveCommissionBreakdown(checkoutItems, commissionData?.itemsCommission),
        grandTotal,
        payableNow,
        value: totalValue,
        carHirePaidOnPickUpFees: adjCarHirePaidOnPickUpFees,
        carHireAddonFees: adjCarHireAddonsFees,
        carHirePayOnArrival: adjCarHirePayOnArrival,
        carHirePickupDate: carHireItems[0]?.pickUpDate,
        ...(velocityPointsApplied && { velocityPointsApplied }),
        paidPrice,
        newPrice,
        merchantFee,
        serviceFee: payableServiceFee,
        isMemberPrice: memberPrice > 0,
        memberPrice,
        surcharge: adjSurcharge,
        mobileAppDiscount: mobileAppDiscountTotals,
        loyaltyBurnTotalInPoints,
        loyaltyBurnTotalInCurrency,
        paymentScheduleAdjustment,
      },
    }
  },
)

export default getCheckoutTotalsView
